import { motion } from 'framer-motion';
import {
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  HardHat,
  Sparkles,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { Container } from '../layout/Container';
import { Reveal } from '../motion/Reveal';
import { Button } from '../ui/button';
import { ROUTES } from '@shared/constants/routes';
import { cn } from '../../lib/cn';
import { easeStripe } from '../../lib/motion';

interface Step {
  num: string;
  duration: string;
  title: string;
  description: string;
  bullets: string[];
  icon: LucideIcon;
  tone: 'accent' | 'brand' | 'lavender';
}

const STEPS: Step[] = [
  {
    num: '01',
    duration: '60 sec',
    title: 'Vous nous décrivez votre projet',
    description:
      "Quelques questions ciblées — situation, surface, énergie, travaux envisagés. Vous repartez immédiatement avec une estimation des aides auxquelles vous avez droit (MaPrimeRénov', primes CEE, éco-PTZ, TVA 5,5 %).",
    bullets: ['Sans inscription', 'Adapté à votre profil', 'Estimation chiffrée'],
    icon: Zap,
    tone: 'accent',
  },
  {
    num: '02',
    duration: '24 à 72 h',
    title: 'On chiffre votre plan d’aides',
    description:
      "Notre équipe (ingénierie + juridique) consolide votre dossier et vous remet un plan d'aides ligne par ligne : montants, conditions, calendrier de versement, plafonds cumulables.",
    bullets: ['Équipe dédiée', 'Sources citées', 'Engagements écrits'],
    icon: ClipboardList,
    tone: 'brand',
  },
  {
    num: '03',
    duration: 'Calendrier officiel',
    title: 'On exécute, vous suivez',
    description:
      "Travaux par notre réseau d'artisans RGE, dossiers d'aides montés et déposés par nos soins. On vous tient informé à chaque étape — versements assurés par les organismes officiels.",
    bullets: ['Réseau RGE partenaires', 'Dossiers déposés pour vous', 'Informé à chaque étape'],
    icon: HardHat,
    tone: 'lavender',
  },
];

const TONE = {
  accent: {
    iconBg: 'bg-accent-50',
    iconText: 'text-accent-700',
    numText: 'text-accent-700',
    badge: 'bg-accent-50 text-accent-800 ring-accent-200/60',
    line: 'from-accent-500 to-brand-500',
  },
  brand: {
    iconBg: 'bg-brand-50',
    iconText: 'text-brand-700',
    numText: 'text-brand-700',
    badge: 'bg-brand-50 text-brand-700 ring-brand-200/60',
    line: 'from-brand-500 to-lavender-500',
  },
  lavender: {
    iconBg: 'bg-lavender-50',
    iconText: 'text-lavender-700',
    numText: 'text-lavender-700',
    badge: 'bg-lavender-50 text-lavender-700 ring-lavender-200/60',
    line: 'from-lavender-500 to-lavender-500',
  },
} as const;

export function HowItWorks() {
  return (
    <section
      aria-labelledby="how-it-works-title"
      className="relative bg-stone-50/40 py-16 md:py-24"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-500/30 to-transparent"
      />

      <Container>
        <Reveal>
          <div className="mx-auto mb-12 max-w-2xl text-center md:mb-14">
            <p className="eyebrow justify-center">Notre méthode</p>
            <h2
              id="how-it-works-title"
              className="mt-3 text-balance font-display text-3xl font-bold tracking-tight text-ink md:text-4xl"
            >
              Du premier clic au dernier versement,{' '}
              <span className="text-gradient">trois étapes</span>.
            </h2>
            <p className="mt-3 text-[15px] leading-relaxed text-stone-600">
              Vous ne signez qu’en connaissance de cause. Tout est chiffré, sourcé, daté — y compris
              le calendrier de versement des primes.
            </p>
          </div>
        </Reveal>

        {/* Desktop : grid 3 colonnes avec ligne connectrice */}
        <div className="relative">
          {/* Ligne connectrice horizontale (desktop) */}
          <div
            aria-hidden
            className="pointer-events-none absolute left-0 right-0 top-[3.25rem] hidden h-px lg:block"
          >
            <div className="mx-auto h-full w-2/3 bg-gradient-to-r from-transparent via-stone-300 to-transparent" />
          </div>

          <ol className="grid gap-3 lg:grid-cols-3">
            {STEPS.map((step, idx) => {
              const Icon = step.icon;
              const t = TONE[step.tone];
              return (
                <motion.li
                  key={step.num}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.45, delay: idx * 0.08, ease: easeStripe }}
                  className="relative flex flex-col gap-4 rounded-2xl border border-stone-200/70 bg-paper p-5 shadow-[0_1px_2px_rgba(10,10,10,.04)] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-elevated md:p-6"
                >
                  {/* Pastille icône + N° */}
                  <div className="flex items-start justify-between">
                    <div className="relative">
                      <span
                        className={cn(
                          'flex h-12 w-12 items-center justify-center rounded-xl ring-4 ring-stone-50/40',
                          t.iconBg,
                          t.iconText,
                        )}
                      >
                        <Icon className="h-5 w-5" strokeWidth={1.6} />
                      </span>
                      {/* glow pulse pour étape 1 */}
                      {idx === 0 && (
                        <span
                          aria-hidden
                          className="absolute -inset-1 -z-10 animate-glow-pulse rounded-2xl bg-accent-300/40 blur-md"
                        />
                      )}
                    </div>
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold ring-1 ring-inset',
                        t.badge,
                      )}
                    >
                      <Sparkles className="h-2.5 w-2.5" />
                      {step.duration}
                    </span>
                  </div>

                  <div>
                    <p
                      className={cn('font-mono text-[11px] font-bold tracking-[0.14em]', t.numText)}
                    >
                      ÉTAPE {step.num}
                    </p>
                    <h3 className="mt-1 font-display text-lg font-semibold leading-tight text-ink">
                      {step.title}
                    </h3>
                    <p className="mt-2 text-[13.5px] leading-relaxed text-stone-600">
                      {step.description}
                    </p>
                  </div>

                  <ul className="mt-auto space-y-1.5 border-t border-stone-200/60 pt-3">
                    {step.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-[12.5px] text-stone-700">
                        <CheckCircle2
                          className={cn('mt-0.5 h-3 w-3 shrink-0', t.iconText)}
                          strokeWidth={2}
                        />
                        {b}
                      </li>
                    ))}
                  </ul>
                </motion.li>
              );
            })}
          </ol>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-10 flex flex-col items-center gap-2 sm:flex-row sm:justify-center">
            <Button asChild size="lg" variant="primary" className="group">
              <a href={ROUTES.outils}>
                Découvrir nos outils gratuits
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5" />
              </a>
            </Button>
            <Button asChild size="lg" variant="secondary" className="group">
              <a href={ROUTES.aPropos}>En savoir plus sur notre approche</a>
            </Button>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}
